import { useStyletron } from '@thanx/uikit/theme'
import Badge from 'components/Badge'
import { buildTranslate } from 'locales'
import moment from 'moment-timezone'
import React from 'react'
import { ContentBlockData } from 'slices/appLandingPageTheme/types'
import { merchantTheme } from 'theme'
import Description from './Description'
import ExternalFormDescription from './ExternalFormDescription'
import FeaturedContentCarouselDescription from './FeaturedContentCarouselDescription'
import FeaturedContentDescription from './FeaturedContentDescription'
import GameDescription from './GameDescription'
import Header from './Header'
import PersonalizedTextDescription from './PersonalizedTextDescription'

const t = buildTranslate('cms.content.app_home_page.content.block_item')

type Props = {
  id: string
  index: number
  data: ContentBlockData
  onEdit: (id: string) => void
  onRemove?: (id: string) => void
  expired?: boolean
}

const BlockItem = ({ id, data, onEdit, onRemove, expired = false }: Props) => {
  const [css] = useStyletron()
  const { type } = data

  const handleEdit = () => onEdit(id)
  const handleRemove = onRemove ? () => onRemove(id) : undefined

  const getDaysUntilStart = (startDate: string) => {
    const start = moment.utc(startDate).startOf('day')
    const now = moment.utc().startOf('day')
    const days = start.diff(now, 'days')
    return days > 0 ? days : 0
  }

  const renderGoLiveBadge = () => {
    const startDate = 'start_at' in data && data.start_at

    if (!startDate) {
      return null
    }

    const daysUntil = getDaysUntilStart(startDate)
    if (daysUntil <= 0) return null

    return (
      <div className="mb-2">
        <Badge
          value={`Go live in ${daysUntil} ${daysUntil === 1 ? 'day' : 'days'}`}
          className={css({
            backgroundColor: merchantTheme.colors.belizeHole10,
            color: merchantTheme.colors.belizeHole50,
          })}
        />
      </div>
    )
  }

  const renderExpiredBadge = () => {
    if (!expired) {
      return null
    }

    return (
      <div className="mb-2">
        <Badge
          value={t('expired')}
          className={css({
            backgroundColor: '#FFFFFF',
            border: `1px solid ${merchantTheme.colors.grey20}`,
            color: merchantTheme.colors.grey70,
          })}
        />
      </div>
    )
  }

  const renderDescription = () => {
    switch (type) {
      case 'featured_content':
        return <FeaturedContentDescription data={data} onEdit={handleEdit} />
      case 'external_form':
        return <ExternalFormDescription data={data} onEdit={handleEdit} />
      case 'personalized_text':
        return <PersonalizedTextDescription data={data} onEdit={handleEdit} />
      case 'featured_content_carousel':
        return (
          <FeaturedContentCarouselDescription
            data={data}
            onEdit={handleEdit}
            carouselId={id}
          />
        )
      case 'game':
        return <GameDescription contentBlock={data} />
      default:
        return <Description type={type} data={data} />
    }
  }

  return (
    <>
      {renderExpiredBadge()}
      {renderGoLiveBadge()}
      <Header type={type} onEdit={handleEdit} onRemove={handleRemove} />
      {renderDescription()}
    </>
  )
}

export default BlockItem
